import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { addFacebookExtraReducers } from './facebook.matchers';
import { addGoogleExtraReducers } from './google.matchers';

export interface QueryFilter {
  id: number;
  field: string;
  operator: string;
  value: any;
}

export interface ClientIntegrationPlatformConfig {
  configIdentifier: string;
  queryFilters: QueryFilter[];
}

export interface AccountLookUp {
  id: string;
  accountId: string;
}

export interface ClientIntegrationPlatformConfigByType {
  platformType: string;
  accountLookUps: AccountLookUp[];
  platformConfig: ClientIntegrationPlatformConfig[];
}

export interface ClientIntegrationPlatformConfigState {
  platformConfigs: ClientIntegrationPlatformConfigByType[];
}

const defaultPlatformConfig: ClientIntegrationPlatformConfig = {
  configIdentifier: '',
  queryFilters: []
};

const initialState: ClientIntegrationPlatformConfigState = {
  platformConfigs: [
    {
      platformType: 'FacebookAds',
      platformConfig: [defaultPlatformConfig],
      accountLookUps: []
    },
    {
      platformType: 'GoogleAds',
      platformConfig: [defaultPlatformConfig],
      accountLookUps: []
    }
  ]
};

const platformConfigSlice = createSlice({
  name: 'platformConfig',
  initialState,
  reducers: {
    addAccountId: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        newAccountId: string;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      if (platformConfig) {
        platformConfig.platformConfig.push({
          configIdentifier: payload.newAccountId,
          queryFilters: []
        });
      }
    },
    removeConfig: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        accountId: string;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      if (platformConfig) {
        const configToUpdate = platformConfig?.platformConfig.filter(
          (accountConfig) =>
            accountConfig.configIdentifier !== payload.accountId
        );

        platformConfig.platformConfig = configToUpdate;
      }
    },
    updateAccountId: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        originalAccountId: string;
        newAccountId: string;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      const configToUpdate = platformConfig?.platformConfig.find(
        (accountConfig) =>
          accountConfig.configIdentifier === payload.originalAccountId
      );
      if (configToUpdate) {
        configToUpdate.configIdentifier = payload.newAccountId;
      }
    },
    addQueryFilter: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        accountId: string;
        queryFilter: QueryFilter;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      const configToUpdate = platformConfig?.platformConfig.find(
        (accountConfig) => accountConfig.configIdentifier === payload.accountId
      );
      if (configToUpdate) {
        configToUpdate.queryFilters.push(payload.queryFilter);
      }
    },
    updateQueryFilter: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        accountId: string;
        updatedQueryFilter: QueryFilter;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      const configToUpdate = platformConfig?.platformConfig.find(
        (accountConfig) => accountConfig.configIdentifier === payload.accountId
      );

      const queryFilterToUpdate = configToUpdate?.queryFilters.find(
        (filter) => filter.id === payload.updatedQueryFilter.id
      );
      if (queryFilterToUpdate) {
        queryFilterToUpdate.field = payload.updatedQueryFilter.field;
        queryFilterToUpdate.operator = payload.updatedQueryFilter.operator;
        queryFilterToUpdate.value = payload.updatedQueryFilter.value;
      }
    },
    removeQueryFilter: (
      state,
      {
        payload
      }: PayloadAction<{
        platformType: string;
        accountId: string;
        filterId: number;
      }>
    ) => {
      const platformConfig = state.platformConfigs.find(
        (config) => config.platformType === payload.platformType
      );
      const configToUpdate = platformConfig?.platformConfig.find(
        (accountConfig) => accountConfig.configIdentifier === payload.accountId
      );
      if (configToUpdate) {
        const queryFilterToUpdate = configToUpdate?.queryFilters.filter(
          (filter) => filter.id !== payload.filterId
        );
        configToUpdate.queryFilters = queryFilterToUpdate;
      }
    }
  },
  extraReducers: (builder) => {
    addFacebookExtraReducers(builder);
    addGoogleExtraReducers(builder);
  }
});

const { actions, reducer } = platformConfigSlice;

export const {
  addAccountId,
  updateAccountId,
  addQueryFilter,
  updateQueryFilter,
  removeConfig,
  removeQueryFilter
} = actions;

export default reducer;

export const selectPlatformConfig = (platformType: string) =>
  createSelector(
    (state: RootState) => state.platformConfig,
    (platformConfigState: ClientIntegrationPlatformConfigState) =>
      platformConfigState.platformConfigs.find(
        (config: ClientIntegrationPlatformConfigByType) =>
          config.platformType === platformType
      )?.platformConfig ?? []
  );

export const selectPlatformConfigAccountIds = (platformType: string) =>
  createSelector(
    (state: RootState) => state.platformConfig,
    (platformConfigState: ClientIntegrationPlatformConfigState) =>
      platformConfigState.platformConfigs
        .find(
          (config: ClientIntegrationPlatformConfigByType) =>
            config.platformType === platformType
        )
        ?.platformConfig.map(
          (accountConfig) => accountConfig.configIdentifier
        ) ?? []
  );
